import { useState } from "react";
import { Calendar } from "lucide-react";

interface PeriodFilterProps {
  startDate: string;
  endDate: string;
  onChange: (startDate: string, endDate: string) => void;
}

const formatDisplay = (value: string) =>
  new Intl.DateTimeFormat("id-ID", {
    day: "2-digit",
    month: "long",
    year: "numeric",
  }).format(new Date(value));

export default function PeriodFilter({
  startDate,
  endDate,
  onChange,
}: PeriodFilterProps) {
  const [open, setOpen] = useState(false);
  const [tempStart, setTempStart] = useState(startDate);
  const [tempEnd, setTempEnd] = useState(endDate);
  const [error, setError] = useState("");

  const validatePeriod = (start: string, end: string) => {
    const startDate = new Date(start);
    const endDate = new Date(end);

    if (endDate < startDate) {
      return "Tanggal selesai tidak boleh lebih kecil dari tanggal mulai.";
    }

    const months =
      (endDate.getFullYear() - startDate.getFullYear()) * 12 +
      (endDate.getMonth() - startDate.getMonth());

    if (
      months > 12 ||
      (months === 12 && endDate.getDate() > startDate.getDate())
    ) {
      return "Rentang periode maksimal 12 bulan.";
    }

    return "";
  };

  const handleApply = () => {
    const validation = validatePeriod(tempStart, tempEnd);

    if (validation) {
      setError(validation);
      return;
    }

    setError("");
    onChange(tempStart, tempEnd);
    setOpen(false);
  };

  const handleStartChange = (value: string) => {
    setTempStart(value);
    setError(validatePeriod(value, tempEnd));
  };

  const handleEndChange = (value: string) => {
    setTempEnd(value);
    setError(validatePeriod(tempStart, value));
  };

  return (
    <div className="relative rounded-2xl bg-white p-5 shadow-sm">
      <div className="flex items-center justify-between">
        <span className="font-semibold text-[#324A5A]">Periode</span>

        <button
          onClick={() => setOpen((prev) => !prev)}
          className="flex items-center gap-3 text-sm font-medium text-[#324A5A] hover:text-[#3DA4A8]">
          {formatDisplay(startDate)} - {formatDisplay(endDate)}
          <Calendar size={20} className="text-[#3DA4A8]" />
        </button>
      </div>

      {open && (
        <div className="absolute right-0 top-full z-30 mt-2 w-80 rounded-2xl bg-white p-5 shadow-xl">
          <div className="space-y-3">
            <div>
              <label className="mb-1 block text-xs text-slate-400">Dari</label>
              <input
                type="date"
                value={tempStart}
                onChange={(e) => handleStartChange(e.target.value)}
                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm outline-none focus:border-[#3DA4A8]"
              />
            </div>
            <div>
              <label className="mb-1 block text-xs text-slate-400">
                Sampai
              </label>
              <input
                type="date"
                value={tempEnd}
                onChange={(e) => handleEndChange(e.target.value)}
                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm outline-none focus:border-[#3DA4A8]"
              />
              {error && <p className="mt-3 text-sm text-red-500">{error}</p>}
            </div>
          </div>

          <button
            onClick={handleApply}
            disabled={!!error}
            className={`mt-4 w-full rounded-xl py-2.5 text-sm font-semibold text-white
              ${
                error
                  ? "cursor-not-allowed bg-gray-300"
                  : "bg-[#3DA4A8] hover:bg-[#2F8C90]"
              }`}>
            Terapkan
          </button>
        </div>
      )}
    </div>
  );
}
